"use client";

import { useState, useRef, Suspense } from "react";
import { useTranslations } from "next-intl";
import { useRouter, usePathname } from "@/i18n/navigation";
import { useSearchParams } from "next/navigation";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  charts, reports, compatibility,
  type Chart, type Report, type CompatibilityReport, type CreateChartInput,
  ApiError,
} from "@/lib/api";
import ChartForm from "@/components/astrology/ChartForm";
import ExtraOptionsForm, { type ExtraOptions } from "@/components/astrology/ExtraOptionsForm";
import ReportViewer from "@/components/astrology/ReportViewer";
import type { ReportType } from "./page";

// ── Report kind classification ────────────────────────────────────────────────

interface ReportConfig {
  kind: "single" | "two-person";
  needsReturnYear?: boolean;
  needsReturnLocation?: boolean;
  needsProgressionYear?: boolean;
  yearLabel?: string;
}

const REPORT_CONFIG: Record<string, ReportConfig> = {
  "solar-return":    { kind: "single", needsReturnYear: true, needsReturnLocation: true, yearLabel: "Year of solar return" },
  "lunar-return":    { kind: "single", needsReturnYear: true, yearLabel: "Month/year of lunar return (enter the year)" },
  "yearly-forecast": { kind: "single", needsProgressionYear: true, yearLabel: "Year to forecast" },
  "progressed":      { kind: "single", needsProgressionYear: true, yearLabel: "Year to progress to" },
  "synastry":        { kind: "two-person" },
  "marriage":        { kind: "two-person" },
  "composite":       { kind: "two-person" },
};

function getConfig(reportType: string): ReportConfig {
  return REPORT_CONFIG[reportType] ?? { kind: "single" };
}

// ── Steps ─────────────────────────────────────────────────────────────────────

type Step = "info" | "form-a" | "form-b" | "extra" | "generating" | "report";

// ── Inner component ───────────────────────────────────────────────────────────

function ReportDetailInner({ reportType }: { reportType: ReportType }) {
  const t = useTranslations("reports");
  const te = useTranslations("errors");
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const config = getConfig(reportType);
  const needsExtra = !!(config.needsReturnYear || config.needsProgressionYear);

  const reportIdFromUrl = searchParams.get("reportId");
  const [step, setStep] = useState<Step>(reportIdFromUrl ? "report" : "info");
  const [reportId, setReportId] = useState<string | null>(reportIdFromUrl);
  const [error, setError] = useState<string | null>(null);
  const [generatedReport, setGeneratedReport] = useState<Report | CompatibilityReport | null>(null);

  // For two-person reports: chart A is fetched in the background while Person B form is shown
  const chartAPromise = useRef<Promise<Chart> | null>(null);

  const { data: fetchedReport, isError: fetchError } = useQuery({
    queryKey: ["report", reportId],
    queryFn: () => reports.get(reportId!),
    enabled: !!reportId,
    staleTime: Infinity,
    retry: false,
  });

  // If a reportId from URL 404s, fall back to info
  useState(() => {
    if (fetchError) {
      setReportId(null);
      setStep("info");
      router.replace(pathname);
    }
  });

  function setReportAndUpdateUrl(id: string) {
    setReportId(id);
    const params = new URLSearchParams(searchParams.toString());
    params.set("reportId", id);
    router.replace(`${pathname}?${params.toString()}`);
    setStep("report");
  }

  function handleError(err: unknown) {
    setError(err instanceof ApiError ? err.message : te("generic"));
  }

  // ── Single-person flow ──────────────────────────────────────────────────────

  // Holds chart from form-a while waiting for extra options
  const [pendingChart, setPendingChart] = useState<Chart | null>(null);

  const reportMutation = useMutation({
    mutationFn: reports.create,
    onSuccess: (report: Report) => {
      setGeneratedReport(report);
      setReportAndUpdateUrl(report.reportId);
    },
    onError: (err) => { handleError(err); setStep(needsExtra ? "extra" : "form-a"); },
  });

  const singleChartMutation = useMutation({
    mutationFn: charts.create,
    onSuccess: (chart: Chart) => {
      if (needsExtra) {
        setPendingChart(chart);
        setStep("extra");
      } else {
        reportMutation.mutate({ chartId: Number(chart.chartId), reportType, tier: "bronze", includeIntro: true, includeOutro: true, includeImages: true, includeQuotes: true });
      }
    },
    onError: (err) => { handleError(err); setStep("form-a"); },
  });

  function handleSingleFormSubmit(data: CreateChartInput) {
    setStep("generating");
    setError(null);
    singleChartMutation.mutate(data);
  }

  function handleExtraOptions(opts: ExtraOptions) {
    if (!pendingChart) return;
    setStep("generating");
    reportMutation.mutate({
      chartId: Number(pendingChart.chartId),
      reportType,
      tier: "bronze",
      includeIntro: true,
      includeOutro: true,
      includeImages: true,
      includeQuotes: true,
      ...(opts.year && config.needsReturnYear    ? { returnYear: opts.year }       : {}),
      ...(opts.year && config.needsProgressionYear ? { progressionYear: opts.year } : {}),
      ...(opts.returnLatitude  != null ? { returnLatitude:  opts.returnLatitude  } : {}),
      ...(opts.returnLongitude != null ? { returnLongitude: opts.returnLongitude } : {}),
    });
  }

  // ── Two-person flow ─────────────────────────────────────────────────────────

  const compatibilityMutation = useMutation({
    mutationFn: compatibility.create,
    onSuccess: (report: CompatibilityReport) => {
      setGeneratedReport(report);
      setReportAndUpdateUrl(report.reportId);
    },
    onError: (err) => { handleError(err); setStep("form-b"); },
  });

  function handlePersonASubmit(data: CreateChartInput) {
    setError(null);
    // Fire chart A creation in the background and go straight to Person B form
    chartAPromise.current = charts.create(data);
    setStep("form-b");
  }

  async function handlePersonBSubmit(data: CreateChartInput) {
    setStep("generating");
    setError(null);
    try {
      const [chartA, chartB] = await Promise.all([
        chartAPromise.current!,
        charts.create(data),
      ]);
      compatibilityMutation.mutate({
        chartIdA: Number(chartA.chartId),
        chartIdB: Number(chartB.chartId),
        reportType,
        tier: "bronze",
        includeIntro: true,
        includeOutro: true,
        includeImages: true,
        includeQuotes: true,
      });
    } catch (err) {
      handleError(err);
      setStep("form-b");
    }
  }

  // ── Derived state ───────────────────────────────────────────────────────────

  const isLoading =
    singleChartMutation.isPending ||
    reportMutation.isPending ||
    compatibilityMutation.isPending;

  const activeReport = generatedReport ?? fetchedReport ?? null;
  const paragraphs = t(`${reportType}.description`).split("\n\n").filter(Boolean);

  function handleBack() {
    setReportId(null);
    setGeneratedReport(null);
    chartAPromise.current = null;
    setPendingChart(null);
    setStep("info");
    router.replace(pathname);
  }

  const defaultYear = new Date().getFullYear();

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <button
        onClick={() => {
          if (step === "info") router.push("/charts-and-reports");
          else if (step === "report") handleBack();
          else if (step === "form-b") setStep("form-a");
          else if (step === "extra") setStep("form-a");
          else setStep("info");
        }}
        className="mb-6 text-sm text-indigo-500 hover:text-indigo-700 flex items-center gap-1"
      >
        ← {step === "info" ? "All reports" : "Back"}
      </button>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* ── Info step ── */}
      {step === "info" && (
        <>
          <p className="text-sm text-indigo-500 uppercase tracking-widest mb-2">
            {t(`persons.${t(`${reportType}.persons`)}`)}
          </p>
          <h1 className="text-4xl font-bold text-indigo-900 mb-3">{t(`${reportType}.title`)}</h1>
          <p className="text-lg text-gray-500 italic mb-8">{t(`${reportType}.summary`)}</p>
          <div className="space-y-4 mb-10">
            {paragraphs.map((para, i) => (
              <p key={i} className="text-gray-700 leading-relaxed">{para}</p>
            ))}
          </div>
          <button
            onClick={() => setStep("form-a")}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded-xl transition-colors"
          >
            {t("tryFree")} — {t(`${reportType}.title`)}
          </button>
        </>
      )}

      {/* ── Form A (single person, or Person A of two) ── */}
      {step === "form-a" && (
        <>
          {config.kind === "two-person" && (
            <div className="mb-6">
              <p className="text-xs text-indigo-400 uppercase tracking-widest mb-3">
                {t(`${reportType}.title`)}
              </p>
              <div className="inline-flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-indigo-600 text-white text-sm font-semibold flex items-center justify-center">1</span>
                  <span className="text-sm font-medium text-indigo-800">Person A</span>
                </div>
                <div className="w-12 h-px bg-gray-200" />
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-gray-200 text-gray-400 text-sm font-semibold flex items-center justify-center">2</span>
                  <span className="text-sm text-gray-400">Person B</span>
                </div>
              </div>
            </div>
          )}
          <h2 className="text-2xl font-semibold text-indigo-800 mb-6">
            {config.kind === "two-person" ? "Person A — Birth Details" : "Enter Birth Details"}
          </h2>
          <ChartForm
            label={config.kind === "two-person" ? "Person A" : undefined}
            onSubmit={config.kind === "two-person" ? handlePersonASubmit : handleSingleFormSubmit}
            isLoading={isLoading}
            submitLabel={config.kind === "two-person" ? "Next: Person B" : undefined}
          />
        </>
      )}

      {/* ── Form B (Person B, two-person only) ── */}
      {step === "form-b" && (
        <>
          <div className="mb-6">
            <p className="text-xs text-indigo-400 uppercase tracking-widest mb-3">
              {t(`${reportType}.title`)}
            </p>
            <div className="inline-flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-indigo-200 text-indigo-600 text-sm font-semibold flex items-center justify-center">1</span>
                <span className="text-sm text-indigo-400">Person A</span>
              </div>
              <div className="w-12 h-px bg-indigo-300" />
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-indigo-600 text-white text-sm font-semibold flex items-center justify-center">2</span>
                <span className="text-sm font-medium text-indigo-800">Person B</span>
              </div>
            </div>
          </div>
          <h2 className="text-2xl font-semibold text-indigo-800 mb-6">Person B — Birth Details</h2>
          <ChartForm
            label="Person B"
            onSubmit={handlePersonBSubmit}
            isLoading={isLoading}
            submitLabel="Generate Report"
          />
        </>
      )}

      {/* ── Extra options (return year / progression year) ── */}
      {step === "extra" && (
        <>
          <h2 className="text-2xl font-semibold text-indigo-800 mb-6">Report Options</h2>
          <ExtraOptionsForm
            needsYear={!!(config.needsReturnYear || config.needsProgressionYear)}
            yearLabel={config.yearLabel ?? "Year"}
            defaultYear={defaultYear}
            needsReturnLocation={!!config.needsReturnLocation}
            onSubmit={handleExtraOptions}
            isLoading={isLoading}
          />
        </>
      )}

      {/* ── Generating spinner ── */}
      {step === "generating" && (
        <div className="text-center py-20 text-gray-500">
          <p className="text-lg">Generating your report…</p>
        </div>
      )}

      {/* ── Report loading ── */}
      {step === "report" && reportId && !activeReport && (
        <div className="text-center py-20 text-gray-400">
          <p>Loading report…</p>
        </div>
      )}

      {/* ── Report display ── */}
      {step === "report" && activeReport && (
        <ReportViewer report={activeReport} onBack={handleBack} />
      )}
    </div>
  );
}

export default function ReportDetail({ reportType }: { reportType: ReportType }) {
  return (
    <Suspense>
      <ReportDetailInner reportType={reportType} />
    </Suspense>
  );
}
